import { type CSSProperties, type ReactNode, useLayoutEffect, useState } from "react"
import styled, { createGlobalStyle, css } from "styled-components"
import type { ApiTypes } from "types"

import {
  formatContextValue,
  formatMeaning,
  formatPriorityNum,
  getMeaningColor,
  MEANING_BG,
  METRIC_LABELS,
  URGENCY_BADGE_COLORS,
} from "../sharedTokens"

type KeyIndicator = ApiTypes.Model.IntelligenceReportIndicator

export const PAGE_WIDTH = 595
export const PAGE_HEIGHT = 842
const HORIZONTAL_PADDING_DESKTOP = 48
const HORIZONTAL_PADDING_MOBILE = 16
const MOBILE_BREAKPOINT = 768
const MAX_SCALE = 1.5

export const pageTypography = css`
  font-family:
    Helvetica,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Arial,
    sans-serif;
  color: #0f172a;
  font-size: 12px;
  line-height: 1.5;
`

export const ReportPrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: ${PAGE_WIDTH}px ${PAGE_HEIGHT}px;
      margin: 0;
    }

    html,
    body {
      background: #ffffff !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
`

const PageCardWrap = styled.div`
  margin: 0 auto 24px;
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-bottom: 12px;
  }

  @media print {
    width: ${PAGE_WIDTH}px !important;
    height: ${PAGE_HEIGHT}px !important;
    margin: 0 !important;
    break-after: page;

    &:last-child {
      break-after: auto;
    }
  }
`

const PageCardInner = styled.div<{ $bg?: string }>`
  width: ${PAGE_WIDTH}px;
  height: ${PAGE_HEIGHT}px;
  transform-origin: top left;
  background: ${p => p.$bg || "#ffffff"};
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;

  @media print {
    transform: none !important;
    box-shadow: none !important;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  ${pageTypography}
`

function usePageScale(): number {
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const compute = () => {
      const padding = window.innerWidth <= MOBILE_BREAKPOINT ? HORIZONTAL_PADDING_MOBILE : HORIZONTAL_PADDING_DESKTOP
      const available = window.innerWidth - padding
      setScale(Math.min(MAX_SCALE, available / PAGE_WIDTH))
    }
    compute()
    window.addEventListener("resize", compute)
    return () => window.removeEventListener("resize", compute)
  }, [])

  return scale
}

interface PageCardProps {
  children: ReactNode
  background?: string
}

export function PageCard({ children, background }: PageCardProps) {
  const scale = usePageScale()
  return (
    <PageCardWrap style={{ width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale }}>
      <PageCardInner $bg={background} style={{ transform: `scale(${scale})` }}>
        {children}
      </PageCardInner>
    </PageCardWrap>
  )
}

// ──────────────────── Header bar (used on Overview / System / Actions pages) ─

export const HeaderBar = styled.div`
  background: #3e4675;
  height: 66px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px;
  margin-bottom: 20px;
`

export const HeaderText = styled.div`
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
`

export const HeaderSubtext = styled.div`
  color: #cbd5e1;
  font-size: 11px;
  margin-top: 4px;
  line-height: 1.5;
`

export const PageContent = styled.div`
  padding: 0 24px;
  display: flex;
  flex-direction: column;
`

// ──────────────────── Bullet list ─────────────────────────────────────────────

const BulletWrap = styled.ul`
  margin: 0 0 6px;
  padding-left: 8px;
  list-style: none;
`

const BulletItem = styled.li`
  display: flex;
  margin-bottom: 2px;
  padding-right: 15px;
`

const BulletDot = styled.span`
  position: relative;
  top: 2px;
  width: 14px;
  font-size: 16px;
  line-height: 1;
`

const BulletText = styled.span`
  flex: 1;
  font-size: 12px;
  line-height: 1.5;
`

export function BulletList({ items }: { items: string[] }) {
  return (
    <BulletWrap>
      {items.map((text, idx) => (
        <BulletItem key={idx}>
          <BulletDot>{"•"}</BulletDot>
          <BulletText>{text}</BulletText>
        </BulletItem>
      ))}
    </BulletWrap>
  )
}

// ──────────────────── Domain section (System Condition pages) ─────────────────

const DomainTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.154px;
  line-height: 1.4;
  margin: 0px 0 4px;
`

const DomainBlockquote = styled.div`
  background: #f1f5f9;
  border-radius: 6px;
  border-left: 4px solid #334155;
  padding: 12px 16px 16px;
  margin-bottom: 4px;
`

const DomainBulletItem = styled.div`
  display: flex;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`

const DomainBulletDot = styled.span`
  position: relative;
  top: -2px;
  width: 14px;
  font-size: 15px;
`

const DomainBulletText = styled.span`
  flex: 1;
  font-size: 11px;
  line-height: 1.23;
  color: #1a2234;
  letter-spacing: -0.1px;
`

const MetricTableWrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin: 6px 0 10px;
  overflow: hidden;
`

const MetricHeaderRow = styled.div`
  display: flex;
  padding: 8px 6px 8px;
  background: #f8fafc;
`

const MetricHeaderCell = styled.div`
  font-size: 10px;
  line-height: 1.33;
  color: #64748b;
  padding: 0 6px;
  text-transform: uppercase;
`

const MetricRow = styled.div`
  display: flex;
  border-bottom: 1px solid #f1f5f9;
  padding: 4px 6px;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`

const MetricCell = styled.div`
  font-size: 12px;
  line-height: 1.33;
  padding: 0 6px;
  color: #334155;
`

const MetricValueCell = styled.div`
  font-size: 11px;
  line-height: 1.33;
  padding: 0 6px;
  color: #0f172a;
`

const MeaningBadgeWrap = styled.span<{ $bg: string; $color: string }>`
  display: flex;
  align-items: center;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  padding: 2px 8px;
  min-height: 18px;
  border-radius: 999px;
  font-size: 10px;
  line-height: 1.3;
`

function MeaningBadge({ ind }: { ind: KeyIndicator }) {
  const textColor = getMeaningColor(ind.meaning_word, ind.metric)
  const bgColor = textColor ? MEANING_BG[textColor] || "#F1F5F9" : "#F1F5F9"
  return (
    <MeaningBadgeWrap $bg={bgColor} $color={textColor || "#64748b"}>
      {formatMeaning(ind)}
    </MeaningBadgeWrap>
  )
}

const DomainSectionWrap = styled.div`
  display: flex;
  flex-direction: column;
`

const METRIC_COL_WIDTHS = { metric: "50%", value: "18%", meaning: "32%" } as const

export function DomainHeaderAndBullets({ title, indicators }: { title: string; indicators: KeyIndicator[] }) {
  const bullets = indicators.filter(ind => ind.bullet_text).map(ind => ind.bullet_text!)
  return (
    <>
      <DomainTitle>{title}</DomainTitle>
      {bullets.length > 0 && (
        <DomainBlockquote>
          {bullets.map((text, idx) => (
            <DomainBulletItem key={idx}>
              <DomainBulletDot>{"•"}</DomainBulletDot>
              <DomainBulletText>{text}</DomainBulletText>
            </DomainBulletItem>
          ))}
        </DomainBlockquote>
      )}
    </>
  )
}

export function DomainMetricTable({ indicators }: { indicators: KeyIndicator[] }) {
  return (
    <MetricTableWrap>
      <MetricHeaderRow>
        <MetricHeaderCell style={{ width: METRIC_COL_WIDTHS.metric }}>METRIC</MetricHeaderCell>
        <MetricHeaderCell style={{ width: METRIC_COL_WIDTHS.value }}>VALUE</MetricHeaderCell>
        <MetricHeaderCell style={{ width: METRIC_COL_WIDTHS.meaning }}>MEANING</MetricHeaderCell>
      </MetricHeaderRow>
      {indicators.map((ind, idx) => (
        <MetricRow key={idx}>
          <MetricCell style={{ width: METRIC_COL_WIDTHS.metric }}>{METRIC_LABELS[ind.metric] || ind.metric}</MetricCell>
          <MetricValueCell style={{ width: METRIC_COL_WIDTHS.value }}>{ind.value_raw}</MetricValueCell>
          <div style={{ width: METRIC_COL_WIDTHS.meaning, padding: "0 6px", display: "flex" }}>
            <MeaningBadge ind={ind} />
          </div>
        </MetricRow>
      ))}
    </MetricTableWrap>
  )
}

export function DomainSection({ title, indicators }: { title: string; indicators: KeyIndicator[] }) {
  return (
    <DomainSectionWrap>
      <DomainHeaderAndBullets title={title} indicators={indicators} />
      <DomainMetricTable indicators={indicators} />
    </DomainSectionWrap>
  )
}

// ──────────────────── Urgency badge (Recommended Actions) ─────────────────────

const UrgencyBadgeWrap = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  padding: 0 8px;
  height: 18px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  margin-right: 12px;
`

export function UrgencyBadge({ code }: { code: string }) {
  const colors = URGENCY_BADGE_COLORS[code] || URGENCY_BADGE_COLORS.monitor
  return (
    <UrgencyBadgeWrap $bg={colors.bg} $color={colors.text}>
      {code.toUpperCase()}
    </UrgencyBadgeWrap>
  )
}

// ──────────────────── Context card (Overview page) ────────────────────────────

const ContextCardWrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 16px 8px;
`

const ContextCardTitle = styled.div`
  font-size: 14px;
  color: #1e293b;
  letter-spacing: -0.154px;
  line-height: 1.71;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
`

const ContextRow = styled.div<{ $divider?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  ${p => p.$divider && "border-top: 1px dashed #E2E8F0;"}
`

const ContextLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 300;
  width: 55%;
  line-height: 1.33;
`

const ContextValue = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
  text-align: right;
  width: 45%;
  line-height: 1.33;
`

interface ContextCardProps {
  title: string
  fields: Array<{ key: string; label: string }>
  data: Record<string, unknown>
  style?: CSSProperties
}

export function ContextCard({ title, fields, data, style }: ContextCardProps) {
  return (
    <ContextCardWrap style={style}>
      <ContextCardTitle>{title}</ContextCardTitle>
      {fields.map((field, idx) => (
        <ContextRow key={field.key} $divider={idx > 0}>
          <ContextLabel>{field.label}</ContextLabel>
          <ContextValue>{formatContextValue(data[field.key])}</ContextValue>
        </ContextRow>
      ))}
    </ContextCardWrap>
  )
}

// ──────────────────── Details card (Overview page) ────────────────────────────

const DetailsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.154px;
  line-height: 1.4;
  margin-bottom: 2px;
`

const DetailsRow = styled.div<{ $divider?: boolean }>`
  display: flex;
  padding: 4px 0;
  ${p => p.$divider && "border-bottom: 1px solid #E2E8F0;"}
`

const DetailsField = styled.div<{ $align?: "left" | "right" }>`
  width: 50%;
  ${p => (p.$align === "right" ? "padding-left: 12px;" : "padding-right: 12px;")}
`

const DetailsLabel = styled.div`
  font-size: 10px;
  color: #64748b;
  letter-spacing: 0.4px;
  line-height: 1.6;
`

const DetailsValue = styled.div`
  font-size: 14px;
  color: #1e293b;
  letter-spacing: -0.154px;
  line-height: 1.43;
`

const PriorityBadgeWrap = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  font-size: 12px;
  font-weight: 700;
  padding: 0 8px;
  height: 20px;
  border-radius: 999px;
  line-height: 1;
`

interface DetailsCardProps {
  report: ApiTypes.Model.IntelligenceReportDetail
  priorityColors: { text: string; bg: string; border: string }
}

export function DetailsCard({ report, priorityColors }: DetailsCardProps) {
  return (
    <div style={{ marginBottom: 4 }}>
      <DetailsTitle>Details</DetailsTitle>

      <DetailsRow>
        <DetailsField>
          <DetailsLabel>THERMOSTAT SN</DetailsLabel>
          <DetailsValue>{report.device_sn}</DetailsValue>
        </DetailsField>
        <DetailsField $align="right">
          <DetailsLabel>PRIORITY</DetailsLabel>
          <PriorityBadgeWrap $bg={priorityColors.bg} $color={priorityColors.text}>
            {formatPriorityNum(report.priority_num)} {"·"} {report.priority.toUpperCase()}
          </PriorityBadgeWrap>
        </DetailsField>
      </DetailsRow>

      <DetailsRow $divider>
        <DetailsField>
          <DetailsLabel>START DATE</DetailsLabel>
          <DetailsValue>{report.start_date}</DetailsValue>
        </DetailsField>
        <DetailsField $align="right">
          <DetailsLabel>END DATE</DetailsLabel>
          <DetailsValue>{report.end_date}</DetailsValue>
        </DetailsField>
      </DetailsRow>

      <DetailsRow>
        <DetailsField>
          <DetailsLabel>CUSTOMER NAME</DetailsLabel>
          <DetailsValue>{report.customer_name || "—"}</DetailsValue>
        </DetailsField>
        <DetailsField $align="right">
          <DetailsLabel>CUSTOMER EMAIL</DetailsLabel>
          <DetailsValue>{report.customer_email || "—"}</DetailsValue>
        </DetailsField>
      </DetailsRow>
    </div>
  )
}
